import { supabase, handleSupabaseError, isSupabaseConfigured } from './index';
import type { Item, Category, Location, User, Merchant, ItemInsert, ItemUpdate, ImprovementRequest, ImprovementRequestInsert, ImprovementRequestUpdate } from './index';

// Storage service for handling file uploads
export const storageService = {
  // Upload a photo to the item-photos bucket
  async uploadItemPhoto(file: File, itemId: string): Promise<string | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot upload photo');
      return null;
    }

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${itemId}-${Date.now()}.${fileExt}`;
      const filePath = `items/${fileName}`;

      // Upload file to storage
      const { data, error } = await supabase.storage
        .from('item-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: publicData } = supabase.storage
        .from('item-photos')
        .getPublicUrl(filePath);
      return publicData.publicUrl;
    } catch (error) {
      console.error('Storage service error:', error);
      handleSupabaseError(error);
      return null;
    }
  },

  // Upload multiple photos for an item
  async uploadItemPhotos(files: File[], itemId: string): Promise<string[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot upload photos');
      return [];
    }

    if (!files || files.length === 0) {
      return [];
    }

    const uploadPromises = files.map(file => this.uploadItemPhoto(file, itemId));
    
    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(url => url !== null) as string[];
      
      // Log upload results
      console.log(`📸 Photo upload results: ${successfulUploads.length}/${files.length} successful`);
      
      if (successfulUploads.length < files.length) {
        const failedCount = files.length - successfulUploads.length;
        console.warn(`⚠️ ${failedCount} photo(s) failed to upload`);
      }
      
      return successfulUploads;
    } catch (error) {
      console.error('Error in batch photo upload:', error);
      // Return empty array if batch upload fails completely
      return [];
    }
  },

  // Delete a photo from storage
  async deleteItemPhoto(photoUrl: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete photo');
      return false;
    }

    try {
      // Extract file path from URL
      const url = new URL(photoUrl);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts.slice(pathParts.indexOf('items')).join('/');

      const { error } = await supabase.storage
        .from('item-photos')
        .remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      return false;
    }
  },

  // Delete multiple photos
  async deleteItemPhotos(photoUrls: string[]): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete photos');
      return false;
    }

    const deletePromises = photoUrls.map(url => this.deleteItemPhoto(url));
    const results = await Promise.all(deletePromises);
    return results.every(result => result === true);
  },

  // Get signed URL for temporary access (if bucket is private)
  async getSignedUrl(filePath: string, expiresIn: number = 3600): Promise<string | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot get signed URL');
      return null;
    }

    try {
      const { data, error } = await supabase.storage
        .from('item-photos')
        .createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  }
};

// Merchants service
export const merchantsService = {
  // Get all merchants
  async getAll(): Promise<Merchant[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get merchant by ID
  async getById(id: string): Promise<Merchant | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning null');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Create new merchant
  async create(merchant: Omit<Merchant, 'id' | 'created_at' | 'updated_at'>): Promise<Merchant | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot create merchant');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('merchants')
        .insert(merchant)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Update merchant
  async update(id: string, updates: Partial<Merchant>): Promise<Merchant | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update merchant');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('merchants')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Delete merchant (soft delete by setting is_active to false)
  async delete(id: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete merchant');
      return false;
    }

    try {
      const { error } = await supabase
        .from('merchants')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
      return false;
    }
  },

  // Hard delete merchant (admin only)
  async hardDelete(id: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete merchant');
      return false;
    }

    try {
      const { error } = await supabase
        .from('merchants')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
      return false;
    }
  },

  // Search merchants
  async search(query: string): Promise<Merchant[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('is_active', true)
        .or(`name.ilike.%${query}%,description.ilike.%${query}%,contact_name.ilike.%${query}%`)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  }
};

// Items service
export const itemsService = {
  // Get all items with relationships
  async getAll(): Promise<Item[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          *,
          category:categories(*),
          location:locations(*),
          merchant:merchants(*),
          created_by_user:users(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get item by ID
  async getById(id: string): Promise<Item | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning null');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          *,
          category:categories(*),
          location:locations(*),
          merchant:merchants(*),
          created_by_user:users(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Create new item with photo upload support
  async create(item: ItemInsert, photos?: File[]): Promise<Item | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot create item');
      return null;
    }

    try {
      // Get current authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ User not authenticated:', authError);
        throw new Error('User not authenticated');
      }

      console.log('🔍 Auth user found:', { id: user.id, email: user.email });

      // Simplified user handling - use auth user ID directly
      let finalUserId = user.id;
      const userEmail = user.email || 'unknown@email.com';

      // Try to ensure user exists in database
      try {
        // First try to get user by ID (primary key, most efficient)
        const { data: existingUser } = await supabase
          .from('users')
          .select('id, email, name, role')
          .eq('id', user.id)
          .single();

        if (!existingUser) {
          console.log('🔄 Creating user record for authenticated user...');
          
          // Create user record with auth user ID
          const { data: newUser, error: createError } = await supabase
            .from('users')
            .insert({
              id: user.id,
              email: userEmail,
              name: user.user_metadata?.name || user.email?.split('@')[0] || 'Unknown User',
              role: 'admin'
            })
            .select()
            .single();

          if (createError && createError.code !== '23505') {
            // If not a duplicate key error, throw it
            throw createError;
          }
          
          if (newUser) {
            console.log('✅ User record created:', newUser.email);
          } else {
            console.log('✅ User record already exists (duplicate key)');
          }
        } else {
          console.log('✅ User record found:', existingUser.email);
        }
      } catch (userError) {
        console.warn('⚠️ User record management failed, proceeding with auth ID:', userError);
        // Continue with item creation even if user record handling fails
      }

      // Validate required fields before creating item
      if (!item.name || !item.category_id || !item.location_id || !item.purchase_date) {
        throw new Error('Missing required fields: name, category_id, location_id, and purchase_date are required');
      }

      // Validate data types
      if (typeof item.purchase_price !== 'number' || item.purchase_price < 0) {
        throw new Error('Purchase price must be a non-negative number');
      }

      // Add created_by field to item data
      const itemWithCreatedBy = {
        ...item,
        created_by: finalUserId,
        // Ensure proper data types
        purchase_price: Number(item.purchase_price),
        name: String(item.name).trim(),
        description: item.description ? String(item.description).trim() : null,
        notes: item.notes ? String(item.notes).trim() : null,
      };

      console.log('🔍 Creating item with data:', itemWithCreatedBy);

      // First create the item
      const { data, error } = await supabase
        .from('items')
        .insert(itemWithCreatedBy)
        .select(`
          *,
          category:categories(*),
          location:locations(*),
          merchant:merchants(*),
          created_by_user:users(*)
        `)
        .single();

      if (error) {
        console.error('❌ Error creating item:', error);
        throw error;
      }

      console.log('✅ Item created successfully:', data);

      // Upload photos if provided (don't fail item creation if photos fail)
      if (photos && photos.length > 0 && data) {
        try {
          console.log(`📸 Uploading ${photos.length} photo(s) for item ${data.id}...`);
          const photoUrls = await storageService.uploadItemPhotos(photos, data.id);
          
          if (photoUrls.length > 0) {
            console.log(`✅ Successfully uploaded ${photoUrls.length} photo(s)`);
            
            // Update the item with photo URLs
            const { data: updatedData, error: updateError } = await supabase
              .from('items')
              .update({ photo_url: photoUrls })
              .eq('id', data.id)
              .select(`
                *,
                category:categories(*),
                location:locations(*),
                merchant:merchants(*),
                created_by_user:users(*)
              `)
              .single();

            if (updateError) {
              console.error('❌ Failed to update item with photo URLs:', updateError);
              // Don't throw - return the item without photos rather than failing completely
            } else {
              return updatedData;
            }
          } else {
            console.warn('⚠️ No photos were successfully uploaded');
          }
        } catch (photoError) {
          console.error('❌ Photo upload failed, but item was created successfully:', photoError);
          // Continue without photos rather than failing the entire operation
        }
      }

      return data;
    } catch (error) {
      console.error('❌ Item creation failed:', error);
      handleSupabaseError(error);
      return null;
    }
  },

  // Update item with photo management
  async update(id: string, updates: ItemUpdate, newPhotos?: File[], deletePhotos?: string[]): Promise<Item | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update item');
      return null;
    }

    try {
      // Get current item to manage photos
      const currentItem = await this.getById(id);
      if (!currentItem) throw new Error('Item not found');

      let currentPhotoUrls: string[] = currentItem.photo_url || [];

      // Delete photos if requested
      if (deletePhotos && deletePhotos.length > 0) {
        await storageService.deleteItemPhotos(deletePhotos);
        currentPhotoUrls = currentPhotoUrls.filter(url => !deletePhotos.includes(url));
      }

      // Upload new photos if provided
      if (newPhotos && newPhotos.length > 0) {
        const newPhotoUrls = await storageService.uploadItemPhotos(newPhotos, id);
        currentPhotoUrls = [...currentPhotoUrls, ...newPhotoUrls];
      }

      // Update item with new photo URLs
      const updatedData = {
        ...updates,
        photo_url: currentPhotoUrls.length > 0 ? currentPhotoUrls : null
      };

      const { data, error } = await supabase
        .from('items')
        .update(updatedData)
        .eq('id', id)
        .select(`
          *,
          category:categories(*),
          location:locations(*),
          merchant:merchants(*),
          created_by_user:users(*)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Delete item
  async delete(id: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete item');
      return false;
    }

    try {
      // Get item to delete associated photos
      const item = await this.getById(id);
      if (item && item.photo_url && item.photo_url.length > 0) {
        await storageService.deleteItemPhotos(item.photo_url);
      }

      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
      return false;
    }
  },

  // Search items
  async search(query: string): Promise<Item[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          *,
          category:categories(*),
          location:locations(*),
          merchant:merchants(*),
          created_by_user:users(*)
        `)
        .or(`name.ilike.%${query}%,description.ilike.%${query}%,notes.ilike.%${query}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },
};

// Categories service
export const categoriesService = {
  async getAll(): Promise<Category[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  async create(category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot create category');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  async update(id: string, updates: Partial<Category>): Promise<Category | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update category');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete category');
      return false;
    }

    try {
      // Check if category has any items
      const { data: items, error: itemsError } = await supabase
        .from('items')
        .select('id')
        .eq('category_id', id);

      if (itemsError) throw itemsError;

      if (items && items.length > 0) {
        throw new Error(`Cannot delete category. It contains ${items.length} items.`);
      }

      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
      return false;
    }
  }
};

// Locations service
export const locationsService = {
  async getAll(): Promise<Location[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  async create(location: Omit<Location, 'id' | 'created_at' | 'updated_at'>): Promise<Location | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot create location');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .insert(location)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  }
};

// Users service
export const usersService = {
  async getAll(): Promise<User[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  async getCurrentUserRole(): Promise<'admin' | 'editor' | 'viewer' | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning null');
      return null;
    }

    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user?.id) {
        return null;
      }

      console.log('🔍 Auth User ID:', user.id);
      console.log('🔍 Auth User Email:', user.email);

      // Try to find user by ID first
      const { data: dataById, error: errorById } = await supabase
        .from('users')
        .select('id, email, role')
        .eq('id', user.id)
        .maybeSingle();

      if (errorById) {
        console.error('Error finding user by ID:', errorById);
      } else {
        console.log('🔍 User found by ID:', dataById);
      }

      if (dataById) {
        return dataById.role;
      }

      // If not found by ID, try to find by email
      if (!user.email) {
        console.log('❌ No email available for user');
        return null;
      }

      console.log('🔄 User not found by ID, trying by email...');
      const { data: dataByEmail, error: errorByEmail } = await supabase
        .from('users')
        .select('id, email, role')
        .eq('email', user.email)
        .maybeSingle();

      if (errorByEmail) {
        console.error('Error finding user by email:', errorByEmail);
        throw errorByEmail;
      }

      console.log('🔍 User found by email:', dataByEmail);

      if (dataByEmail) {
        // If we found the user by email but not by ID, there's a mismatch
        console.warn(`⚠️ User ID mismatch! Auth ID: ${user.id}, DB ID: ${dataByEmail.id}`);
        return dataByEmail.role;
      }

      console.log(`❌ No user record found for: ${user.email || 'unknown'}`);
      return null;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  }
};

// Dashboard stats service
export const dashboardService = {
  async getStats(): Promise<any> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty stats');
      return {
        totalItems: 0,
        totalValue: 0,
        staged: 0,
        inStorage: 0,
        totalCategories: 0,
        totalMerchants: 0,
      };
    }

    try {
      // Get all items for calculations
      const { data: items, error: itemsError } = await supabase
        .from('items')
        .select('purchase_price, status');

      if (itemsError) throw itemsError;

      // Get categories count
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('id');

      if (categoriesError) throw categoriesError;

      // Get active merchants count only
      const { data: merchants, error: merchantsError } = await supabase
        .from('merchants')
        .select('id')
        .eq('is_active', true);

      if (merchantsError) throw merchantsError;

      const totalItems = items?.length || 0;
      const totalValue = items?.reduce((sum, item) => sum + item.purchase_price, 0) || 0;
              const staged = items?.filter(item => item.status === 'staged').length || 0;
      const inStorage = items?.filter(item => item.status === 'in_storage').length || 0;
      const totalCategories = categories?.length || 0;
      const totalActiveMerchants = merchants?.length || 0;

      return {
        totalItems,
        totalValue,
        staged,
        inStorage,
        totalCategories,
        totalMerchants: totalActiveMerchants,
      };
    } catch (error) {
      handleSupabaseError(error);
      return {
        totalItems: 0,
        totalValue: 0,
        staged: 0,
        inStorage: 0,
        totalCategories: 0,
        totalMerchants: 0,
      };
    }
  }
};

// Improvement Requests service
export const improvementRequestsService = {
  // Create a new improvement request
  async create(request: Omit<ImprovementRequestInsert, 'id' | 'created_at' | 'updated_at' | 'status'>): Promise<ImprovementRequest | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot create improvement request');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .insert({
          ...request,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Get all improvement requests visible to the current user
  async getAll(): Promise<ImprovementRequest[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      // Try to get all requests first (this works for admins due to RLS)
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching improvement requests:', error);
        throw error;
      }
      
      console.log('Successfully fetched improvement requests:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error in getAll improvement requests:', error);
      handleSupabaseError(error);
      return [];
    }
  },

  // Get all improvement requests for admin users only
  async getAllForAdmin(): Promise<ImprovementRequest[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get improvement requests by status
  async getByStatus(status: 'pending' | 'under_review' | 'in_progress' | 'completed' | 'rejected' | 'fixed'): Promise<ImprovementRequest[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get improvement requests by category
  async getByCategory(category: 'feature' | 'bug' | 'performance' | 'accessibility' | 'ui'): Promise<ImprovementRequest[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get improvement requests by user email
  async getByUserEmail(userEmail: string): Promise<ImprovementRequest[]> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .eq('user_email', userEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
      return [];
    }
  },

  // Get improvement request by ID
  async getById(id: string): Promise<ImprovementRequest | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning null');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Update improvement request (admin only)
  async update(id: string, updates: ImprovementRequestUpdate): Promise<ImprovementRequest | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update improvement request');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
      return null;
    }
  },

  // Delete improvement request (admin only)
  async delete(id: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot delete improvement request');
      return false;
    }

    try {
      const { error } = await supabase
        .from('improvement_requests')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
      return false;
    }
  },

  // Get improvement requests statistics
  async getStats(): Promise<{
    total: number;
    pending: number;
    under_review: number;
    in_progress: number;
    completed: number;
    rejected: number;
    fixed: number;
    by_category: Record<string, number>;
    by_priority: Record<string, number>;
  }> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty stats');
      return {
        total: 0,
        pending: 0,
        under_review: 0,
        in_progress: 0,
        completed: 0,
        rejected: 0,
        fixed: 0,
        by_category: {},
        by_priority: {}
      };
    }

    try {
      const { data, error } = await supabase
        .from('improvement_requests')
        .select('status, category, priority');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: 0,
        under_review: 0,
        in_progress: 0,
        completed: 0,
        rejected: 0,
        fixed: 0,
        by_category: {} as Record<string, number>,
        by_priority: {} as Record<string, number>
      };

      data?.forEach(request => {
        // Count by status
        if (request.status === 'pending') stats.pending++;
        else if (request.status === 'under_review') stats.under_review++;
        else if (request.status === 'in_progress') stats.in_progress++;
        else if (request.status === 'completed') stats.completed++;
        else if (request.status === 'rejected') stats.rejected++;
        else if (request.status === 'fixed') stats.fixed++;
        
        // Count by category
        stats.by_category[request.category] = (stats.by_category[request.category] || 0) + 1;
        
        // Count by priority
        stats.by_priority[request.priority.toString()] = (stats.by_priority[request.priority.toString()] || 0) + 1;
      });

      return stats;
    } catch (error) {
      handleSupabaseError(error);
      return {
        total: 0,
        pending: 0,
        under_review: 0,
        in_progress: 0,
        completed: 0,
        rejected: 0,
        fixed: 0,
        by_category: {},
        by_priority: {}
      };
    }
  },

  // Update improvement request status and notes (admin only)
  async updateStatus(id: string, update: { status: 'pending' | 'under_review' | 'in_progress' | 'completed' | 'rejected' | 'fixed', admin_notes?: string | null }): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning false');
      return false;
    }

    try {
      console.log('🔄 Updating improvement request:', { id, update });
      
      const { data, error } = await supabase
        .from('improvement_requests')
        .update({
          status: update.status,
          admin_notes: update.admin_notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('❌ Update failed:', error);
        throw error;
      }
      
      console.log('✅ Update successful:', data);
      return true;
    } catch (error) {
      console.error('❌ updateStatus error:', error);
      handleSupabaseError(error);
      return false;
    }
  }
}; 